import React, { createContext, useContext, useState, useEffect } from 'react'

// ─── Supported Languages ──────────────────────────────────────────────────────
//
// US Top Spoken Languages (besides English):
//   Spanish  ~42–49M  |  Chinese ~3.4–3.5M  |  Tagalog ~1.7M
//   Vietnamese ~1.5M  |  Arabic ~1.39M       |  French ~1.18M  |  Korean ~1.07M
//
// Southeast Asia Top Spoken (besides English):
//   Indonesian ~270M  |  Vietnamese ~95M     |  Thai ~60M
//
// Vietnamese appears on both lists → one entry covers both.
// Arabic is RTL — the `dir` property signals right-to-left layout.

// Sorted A–Z by English label
export const LANGUAGES = [
  { code: 'ar', label: 'Arabic',     flag: '🇸🇦', nativeLabel: 'العربية',          dir: 'rtl' },
  { code: 'zh', label: 'Chinese',    flag: '🇨🇳', nativeLabel: '中文',              dir: 'ltr' },
  { code: 'en', label: 'English',    flag: '🇺🇸', nativeLabel: 'English',          dir: 'ltr' },
  { code: 'fr', label: 'French',     flag: '🇫🇷', nativeLabel: 'Français',         dir: 'ltr' },
  { code: 'id', label: 'Indonesian', flag: '🇮🇩', nativeLabel: 'Bahasa Indonesia', dir: 'ltr' },
  { code: 'ko', label: 'Korean',     flag: '🇰🇷', nativeLabel: '한국어',            dir: 'ltr' },
  { code: 'es', label: 'Spanish',    flag: '🇪🇸', nativeLabel: 'Español',          dir: 'ltr' },
  { code: 'tl', label: 'Tagalog',    flag: '🇵🇭', nativeLabel: 'Filipino',         dir: 'ltr' },
  { code: 'th', label: 'Thai',       flag: '🇹🇭', nativeLabel: 'ภาษาไทย',         dir: 'ltr' },
  { code: 'vi', label: 'Vietnamese', flag: '🇻🇳', nativeLabel: 'Tiếng Việt',       dir: 'ltr' },
]

// ─── Translations ─────────────────────────────────────────────────────────────
// Keys are used via  t('key')  throughout the app.
// Fallback chain: requested lang → 'en'
const T = {

  // ── Navigation ──────────────────────────────────────────────────────────────
  nav_home: {
    en: 'Home', es: 'Inicio', zh: '首页', tl: 'Tahanan',
    vi: 'Trang chủ', ar: 'الرئيسية', fr: 'Accueil', ko: '홈',
    id: 'Beranda', th: 'หน้าหลัก',
  },
  nav_my_health: {
    en: 'My Health', es: 'Mi Salud', zh: '我的健康', tl: 'Aking Kalusugan',
    vi: 'Sức khỏe của tôi', ar: 'صحتي', fr: 'Ma Santé', ko: '내 건강',
    id: 'Kesehatanku', th: 'สุขภาพของฉัน',
  },
  nav_exercises: {
    en: 'Exercises', es: 'Ejercicios', zh: '练习', tl: 'Mga Ehersisyo',
    vi: 'Bài tập', ar: 'التمارين', fr: 'Exercices', ko: '운동',
    id: 'Latihan', th: 'การออกกำลังกาย',
  },
  nav_goals: {
    en: 'Goals', es: 'Metas', zh: '目标', tl: 'Mga Layunin',
    vi: 'Mục tiêu', ar: 'الأهداف', fr: 'Objectifs', ko: '목표',
    id: 'Tujuan', th: 'เป้าหมาย',
  },
  nav_injury_explorer: {
    en: 'Injury Explorer', es: 'Explorador de Lesiones', zh: '损伤探索', tl: 'Eksplorasyong Pinsala',
    vi: 'Khám phá Chấn thương', ar: 'استكشاف الإصابات', fr: 'Explorateur de Blessures', ko: '부상 탐색기',
    id: 'Penjelajah Cedera', th: 'สำรวจการบาดเจ็บ',
  },
  nav_messages: {
    en: 'Messages', es: 'Mensajes', zh: '消息', tl: 'Mga Mensahe',
    vi: 'Tin nhắn', ar: 'الرسائل', fr: 'Messages', ko: '메시지',
    id: 'Pesan', th: 'ข้อความ',
  },
  nav_appointments: {
    en: 'Appointments', es: 'Citas', zh: '预约', tl: 'Mga Appointment',
    vi: 'Lịch hẹn', ar: 'المواعيد', fr: 'Rendez-vous', ko: '예약',
    id: 'Janji Temu', th: 'นัดหมาย',
  },
  nav_account: {
    en: 'Account', es: 'Cuenta', zh: '账户', tl: 'Account',
    vi: 'Tài khoản', ar: 'الحساب', fr: 'Compte', ko: '계정',
    id: 'Akun', th: 'บัญชี',
  },
  nav_bills: {
    en: 'Bills', es: 'Facturas', zh: '账单', tl: 'Mga Bill',
    vi: 'Hóa đơn', ar: 'الفواتير', fr: 'Factures', ko: '청구서',
    id: 'Tagihan', th: 'ใบเรียกเก็บเงิน',
  },

  // ── Common actions ───────────────────────────────────────────────────────────
  btn_pay_now: {
    en: 'Pay Now', es: 'Pagar ahora', zh: '立即付款', tl: 'Magbayad Ngayon',
    vi: 'Trả ngay', ar: 'ادفع الآن', fr: 'Payer maintenant', ko: '지금 결제',
    id: 'Bayar Sekarang', th: 'จ่ายเงินตอนนี้',
  },
  btn_view: {
    en: 'View', es: 'Ver', zh: '查看', tl: 'Tingnan',
    vi: 'Xem', ar: 'عرض', fr: 'Voir', ko: '보기',
    id: 'Lihat', th: 'ดู',
  },
  btn_save: {
    en: 'Save', es: 'Guardar', zh: '保存', tl: 'I-save',
    vi: 'Lưu', ar: 'حفظ', fr: 'Enregistrer', ko: '저장',
    id: 'Simpan', th: 'บันทึก',
  },
  btn_cancel: {
    en: 'Cancel', es: 'Cancelar', zh: '取消', tl: 'Kanselahin',
    vi: 'Hủy', ar: 'إلغاء', fr: 'Annuler', ko: '취소',
    id: 'Batal', th: 'ยกเลิก',
  },
  btn_submit: {
    en: 'Submit', es: 'Enviar', zh: '提交', tl: 'Isumite',
    vi: 'Gửi', ar: 'إرسال', fr: 'Soumettre', ko: '제출',
    id: 'Kirim', th: 'ส่ง',
  },
  btn_logout: {
    en: 'Logout', es: 'Cerrar sesión', zh: '退出', tl: 'Mag-logout',
    vi: 'Đăng xuất', ar: 'تسجيل الخروج', fr: 'Déconnexion', ko: '로그아웃',
    id: 'Keluar', th: 'ออกจากระบบ',
  },
  btn_close: {
    en: 'Close', es: 'Cerrar', zh: '关闭', tl: 'Isara',
    vi: 'Đóng', ar: 'إغلاق', fr: 'Fermer', ko: '닫기',
    id: 'Tutup', th: 'ปิด',
  },
  btn_mark_complete: {
    en: 'Mark Complete', es: 'Marcar completado', zh: '标记完成', tl: 'Markahan Kumpleto',
    vi: 'Đánh dấu hoàn thành', ar: 'وضع علامة مكتمل', fr: 'Marquer terminé', ko: '완료 표시',
    id: 'Tandai Selesai', th: 'ทำเครื่องหมายเสร็จสิ้น',
  },

  // ── Status labels ────────────────────────────────────────────────────────────
  status_paid: {
    en: 'Paid', es: 'Pagado', zh: '已付款', tl: 'Nabayaran',
    vi: 'Đã thanh toán', ar: 'مدفوع', fr: 'Payé', ko: '결제 완료',
    id: 'Dibayar', th: 'ชำระแล้ว',
  },
  status_pending: {
    en: 'Pending', es: 'Pendiente', zh: '待处理', tl: 'Nakabinbin',
    vi: 'Đang chờ', ar: 'قيد الانتظار', fr: 'En attente', ko: '대기 중',
    id: 'Tertunda', th: 'รอดำเนินการ',
  },
  status_overdue: {
    en: 'Overdue', es: 'Vencido', zh: '已逾期', tl: 'Nahuling bayad',
    vi: 'Quá hạn', ar: 'متأخر', fr: 'En retard', ko: '연체',
    id: 'Jatuh Tempo', th: 'เกินกำหนด',
  },
  status_cancelled: {
    en: 'Cancelled', es: 'Cancelado', zh: '已取消', tl: 'Kinansela',
    vi: 'Đã hủy', ar: 'ملغى', fr: 'Annulé', ko: '취소됨',
    id: 'Dibatalkan', th: 'ยกเลิกแล้ว',
  },

  // ── Billing – patient view ───────────────────────────────────────────────────
  billing_title: {
    en: 'My Bills', es: 'Mis Facturas', zh: '我的账单', tl: 'Aking mga Bill',
    vi: 'Hóa đơn của tôi', ar: 'فواتيري', fr: 'Mes Factures', ko: '내 청구서',
    id: 'Tagihan Saya', th: 'ใบเรียกเก็บเงินของฉัน',
  },
  billing_subtitle: {
    en: 'View and pay your outstanding balances',
    es: 'Consulte y pague sus saldos pendientes',
    zh: '查看并支付未付款项',
    tl: 'Tingnan at bayaran ang inyong mga natitirang balanse',
    vi: 'Xem và thanh toán số dư còn lại',
    ar: 'عرض وسداد أرصدتك المستحقة',
    fr: 'Consultez et payez vos soldes impayés',
    ko: '미결제 잔액을 확인하고 납부하세요',
    id: 'Lihat dan bayar saldo tertunggak Anda',
    th: 'ดูและชำระยอดค้างชำระของคุณ',
  },
  billing_no_bills: {
    en: 'No bills at this time', es: 'Sin facturas en este momento', zh: '暂无账单',
    tl: 'Walang mga bill sa ngayon', vi: 'Không có hóa đơn lúc này',
    ar: 'لا توجد فواتير في الوقت الحالي', fr: 'Aucune facture pour le moment', ko: '현재 청구서 없음',
    id: 'Tidak ada tagihan saat ini', th: 'ไม่มีใบเรียกเก็บเงินในขณะนี้',
  },
  billing_invoice: {
    en: 'Invoice', es: 'Factura', zh: '发票', tl: 'Invoice',
    vi: 'Hóa đơn', ar: 'فاتورة', fr: 'Facture', ko: '청구서',
    id: 'Faktur', th: 'ใบแจ้งหนี้',
  },
  billing_amount: {
    en: 'Amount', es: 'Importe', zh: '金额', tl: 'Halaga',
    vi: 'Số tiền', ar: 'المبلغ', fr: 'Montant', ko: '금액',
    id: 'Jumlah', th: 'จำนวนเงิน',
  },
  billing_due: {
    en: 'Due', es: 'Vence', zh: '到期', tl: 'Takdang Petsa',
    vi: 'Đến hạn', ar: 'تاريخ الاستحقاق', fr: 'Échéance', ko: '마감일',
    id: 'Jatuh Tempo', th: 'ครบกำหนด',
  },
  billing_description: {
    en: 'Description', es: 'Descripción', zh: '描述', tl: 'Paglalarawan',
    vi: 'Mô tả', ar: 'الوصف', fr: 'Description', ko: '설명',
    id: 'Deskripsi', th: 'รายละเอียด',
  },
  billing_total_due: {
    en: 'Total Due', es: 'Total a pagar', zh: '应付总额', tl: 'Kabuuang Babayaran',
    vi: 'Tổng phải trả', ar: 'إجمالي المستحق', fr: 'Total dû', ko: '납부 총액',
    id: 'Total Tagihan', th: 'ยอดรวมที่ต้องชำระ',
  },
  billing_all_paid: {
    en: "You're all caught up — no outstanding balance!",
    es: '¡Está al día, sin saldo pendiente!',
    zh: '您已全部结清，没有未付款项！',
    tl: 'Naka-abot ka na — walang natitirang balanse!',
    vi: 'Bạn đã thanh toán hết — không còn số dư chưa thanh toán!',
    ar: 'لقد استوفيت جميع المدفوعات — لا يوجد رصيد مستحق!',
    fr: 'Vous êtes à jour — aucun solde impayé !',
    ko: '모두 납부하셨습니다 — 미결제 잔액 없음!',
    id: 'Semua sudah dibayar — tidak ada saldo tertunggak!',
    th: 'คุณชำระเรียบร้อยแล้ว — ไม่มียอดค้างชำระ!',
  },
  billing_payment_processing: {
    en: 'You will be redirected to a secure payment page.',
    es: 'Será redirigido a una página de pago seguro.',
    zh: '您将被重定向到安全支付页面。',
    tl: 'Ire-redirect kayo sa isang ligtas na pahina ng pagbabayad.',
    vi: 'Bạn sẽ được chuyển hướng đến trang thanh toán bảo mật.',
    ar: 'سيتم توجيهك إلى صفحة دفع آمنة.',
    fr: 'Vous serez redirigé vers une page de paiement sécurisée.',
    ko: '보안 결제 페이지로 이동합니다.',
    id: 'Anda akan diarahkan ke halaman pembayaran yang aman.',
    th: 'คุณจะถูกเปลี่ยนเส้นทางไปยังหน้าชำระเงินที่ปลอดภัย',
  },

  // ── Home / recovery ──────────────────────────────────────────────────────────
  home_welcome: {
    en: 'Welcome back', es: 'Bienvenido de nuevo', zh: '欢迎回来', tl: 'Maligayang pagbabalik',
    vi: 'Chào mừng trở lại', ar: 'مرحباً بعودتك', fr: 'Bon retour', ko: '다시 오신 걸 환영합니다',
    id: 'Selamat datang kembali', th: 'ยินดีต้อนรับกลับ',
  },
  home_recovery: {
    en: 'Recovery', es: 'Recuperación', zh: '恢复', tl: 'Pagbawi',
    vi: 'Hồi phục', ar: 'التعافي', fr: 'Rétablissement', ko: '회복',
    id: 'Pemulihan', th: 'การฟื้นตัว',
  },
  home_overview: {
    en: 'Overview', es: 'Resumen', zh: '概览', tl: 'Pangkalahatang-tanaw',
    vi: 'Tổng quan', ar: 'نظرة عامة', fr: 'Aperçu', ko: '개요',
    id: 'Ikhtisar', th: 'ภาพรวม',
  },
  home_today_exercises: {
    en: "Today's exercises", es: 'Ejercicios de hoy', zh: '今日练习', tl: 'Mga ehersisyo ngayon',
    vi: 'Bài tập hôm nay', ar: 'تمارين اليوم', fr: "Exercices d'aujourd'hui", ko: '오늘의 운동',
    id: 'Latihan hari ini', th: 'การออกกำลังกายวันนี้',
  },
  home_upcoming_appts: {
    en: 'Upcoming appointments', es: 'Próximas citas', zh: '即将到来的预约', tl: 'Mga paparating na appointment',
    vi: 'Lịch hẹn sắp tới', ar: 'المواعيد القادمة', fr: 'Prochains rendez-vous', ko: '예정된 예약',
    id: 'Janji temu mendatang', th: 'นัดหมายที่จะถึง',
  },
  home_pain_down: {
    en: 'Pain down', es: 'Dolor reducido', zh: '疼痛减轻', tl: 'Nabawasan ang sakit',
    vi: 'Giảm đau', ar: 'تراجع الألم', fr: 'Douleur réduite', ko: '통증 감소',
    id: 'Nyeri berkurang', th: 'ความเจ็บปวดลดลง',
  },
  home_sessions_left: {
    en: 'sessions left', es: 'sesiones restantes', zh: '剩余疗程', tl: 'sesyon na natitira',
    vi: 'buổi còn lại', ar: 'جلسات متبقية', fr: 'séances restantes', ko: '남은 세션',
    id: 'sesi tersisa', th: 'เซสชั่นที่เหลือ',
  },
  home_no_exercises: {
    en: 'No exercises assigned yet.', es: 'Aún no hay ejercicios asignados.', zh: '尚未分配练习。',
    tl: 'Wala pang nakatalagang mga ehersisyo.', vi: 'Chưa có bài tập nào được giao.',
    ar: 'لم يتم تعيين أي تمارين بعد.', fr: "Aucun exercice assigné pour l'instant.", ko: '아직 배정된 운동이 없습니다.',
    id: 'Belum ada latihan yang diberikan.', th: 'ยังไม่มีการออกกำลังกายที่กำหนด',
  },
  home_no_appointments: {
    en: 'No upcoming appointments.', es: 'No hay próximas citas.', zh: '暂无即将到来的预约。',
    tl: 'Walang paparating na appointment.', vi: 'Không có lịch hẹn sắp tới.',
    ar: 'لا توجد مواعيد قادمة.', fr: 'Aucun rendez-vous à venir.', ko: '예정된 예약이 없습니다.',
    id: 'Tidak ada janji temu mendatang.', th: 'ไม่มีนัดหมายที่จะถึง',
  },

  // ── Health ───────────────────────────────────────────────────────────────────
  health_title: {
    en: 'Recovery Overview', es: 'Resumen de Recuperación', zh: '恢复概览', tl: 'Pangkalahatang-tanaw ng Pagbawi',
    vi: 'Tổng quan Hồi phục', ar: 'نظرة عامة على التعافي', fr: 'Aperçu du rétablissement', ko: '회복 개요',
    id: 'Ikhtisar Pemulihan', th: 'ภาพรวมการฟื้นตัว',
  },
  health_pain_chart: {
    en: 'Pain score over time', es: 'Puntuación del dolor con el tiempo', zh: '疼痛评分随时间变化',
    tl: 'Puntos ng sakit sa paglipas ng panahon', vi: 'Điểm đau theo thời gian',
    ar: 'درجة الألم عبر الزمن', fr: 'Score de douleur dans le temps', ko: '시간에 따른 통증 점수',
    id: 'Skor nyeri dari waktu ke waktu', th: 'คะแนนความเจ็บปวดตามเวลา',
  },
  health_lower_better: {
    en: 'Lower is better', es: 'Más bajo es mejor', zh: '越低越好', tl: 'Mas mababa ay mas mabuti',
    vi: 'Thấp hơn là tốt hơn', ar: 'كلما انخفض كان أفضل', fr: 'Plus c\'est bas, mieux c\'est', ko: '낮을수록 좋습니다',
    id: 'Lebih rendah lebih baik', th: 'ยิ่งต่ำยิ่งดี',
  },
  health_session_notes: {
    en: 'Session notes', es: 'Notas de sesión', zh: '疗程笔记', tl: 'Mga tala ng sesyon',
    vi: 'Ghi chú buổi học', ar: 'ملاحظات الجلسة', fr: 'Notes de séance', ko: '세션 메모',
    id: 'Catatan sesi', th: 'บันทึกเซสชั่น',
  },

  // ── Exercises ────────────────────────────────────────────────────────────────
  exercises_title: {
    en: 'Your Home Program', es: 'Su Programa en Casa', zh: '您的居家训练计划', tl: 'Inyong Home Program',
    vi: 'Chương trình Tại nhà', ar: 'برنامجك المنزلي', fr: 'Votre programme à domicile', ko: '홈 프로그램',
    id: 'Program Rumah Anda', th: 'โปรแกรมที่บ้านของคุณ',
  },
  exercises_done_today: {
    en: 'Done today', es: 'Completado hoy', zh: '今日完成', tl: 'Tapos na ngayon',
    vi: 'Hoàn thành hôm nay', ar: 'تم اليوم', fr: "Terminé aujourd'hui", ko: '오늘 완료',
    id: 'Selesai hari ini', th: 'เสร็จวันนี้',
  },
  exercises_log: {
    en: 'Log Exercise', es: 'Registrar ejercicio', zh: '记录练习', tl: 'I-log ang Ehersisyo',
    vi: 'Ghi chép Bài tập', ar: 'تسجيل التمرين', fr: 'Enregistrer l\'exercice', ko: '운동 기록',
    id: 'Catat Latihan', th: 'บันทึกการออกกำลังกาย',
  },
  exercises_how_hard: {
    en: 'How difficult was it?', es: '¿Qué tan difícil fue?', zh: '难度如何？', tl: 'Gaano kahirap?',
    vi: 'Mức độ khó?', ar: 'كم كانت صعوبته؟', fr: 'Quelle était la difficulté ?', ko: '얼마나 어려웠나요?',
    id: 'Seberapa sulit?', th: 'ยากแค่ไหน?',
  },
  exercises_notes_opt: {
    en: 'Notes (optional)', es: 'Notas (opcional)', zh: '备注（可选）', tl: 'Mga tala (opsyonal)',
    vi: 'Ghi chú (tùy chọn)', ar: 'ملاحظات (اختياري)', fr: 'Notes (facultatif)', ko: '메모 (선택)',
    id: 'Catatan (opsional)', th: 'หมายเหตุ (ถ้ามี)',
  },
  exercises_easy: {
    en: 'Easy', es: 'Fácil', zh: '简单', tl: 'Madali',
    vi: 'Dễ', ar: 'سهل', fr: 'Facile', ko: '쉬움',
    id: 'Mudah', th: 'ง่าย',
  },
  exercises_very_hard: {
    en: 'Very hard', es: 'Muy difícil', zh: '非常难', tl: 'Napakahirap',
    vi: 'Rất khó', ar: 'صعب جداً', fr: 'Très difficile', ko: '매우 어려움',
    id: 'Sangat sulit', th: 'ยากมาก',
  },
  exercises_sets_reps: {
    en: 'sets × reps', es: 'series × reps', zh: '组 × 次', tl: 'sets × reps',
    vi: 'hiệp × lần', ar: 'مجموعات × تكرارات', fr: 'séries × répétitions', ko: '세트 × 횟수',
    id: 'set × repetisi', th: 'เซต × ครั้ง',
  },
  exercises_logged_today: {
    en: 'Logged today!', es: '¡Registrado hoy!', zh: '今日已记录！', tl: 'Na-log ngayon!',
    vi: 'Đã ghi hôm nay!', ar: 'تم التسجيل اليوم!', fr: "Enregistré aujourd'hui !", ko: '오늘 기록 완료!',
    id: 'Sudah dicatat hari ini!', th: 'บันทึกวันนี้แล้ว!',
  },
  exercises_log_it: {
    en: 'Log it', es: 'Registrarlo', zh: '记录', tl: 'I-log',
    vi: 'Ghi chép', ar: 'سجّله', fr: 'Enregistrer', ko: '기록',
    id: 'Catat', th: 'บันทึก',
  },

  // ── Goals ────────────────────────────────────────────────────────────────────
  goals_title: {
    en: 'Your Goals', es: 'Sus Metas', zh: '您的目标', tl: 'Inyong mga Layunin',
    vi: 'Mục tiêu của bạn', ar: 'أهدافك', fr: 'Vos objectifs', ko: '내 목표',
    id: 'Tujuan Anda', th: 'เป้าหมายของคุณ',
  },
  goals_completed: {
    en: 'completed', es: 'completado', zh: '已完成', tl: 'nakumpleto',
    vi: 'hoàn thành', ar: 'مكتمل', fr: 'terminé', ko: '완료됨',
    id: 'selesai', th: 'เสร็จสิ้น',
  },
  goals_empty: {
    en: 'No goals set yet.', es: 'Aún no hay metas establecidas.', zh: '尚未设置目标。',
    tl: 'Walang itinakdang mga layunin pa.', vi: 'Chưa đặt mục tiêu.',
    ar: 'لم يتم تحديد أهداف بعد.', fr: 'Aucun objectif fixé pour le moment.', ko: '아직 설정된 목표가 없습니다.',
    id: 'Belum ada tujuan.', th: 'ยังไม่ได้กำหนดเป้าหมาย',
  },
  goals_set_by: {
    en: 'Set by', es: 'Establecido por', zh: '设定人', tl: 'Itinakda ng',
    vi: 'Được đặt bởi', ar: 'حُدد بواسطة', fr: 'Défini par', ko: '설정자',
    id: 'Ditetapkan oleh', th: 'กำหนดโดย',
  },

  // ── Messages ─────────────────────────────────────────────────────────────────
  messages_title: {
    en: 'Messages', es: 'Mensajes', zh: '消息', tl: 'Mga Mensahe',
    vi: 'Tin nhắn', ar: 'الرسائل', fr: 'Messages', ko: '메시지',
    id: 'Pesan', th: 'ข้อความ',
  },
  messages_placeholder: {
    en: 'Type a message…', es: 'Escriba un mensaje…', zh: '输入消息…', tl: 'Mag-type ng mensahe…',
    vi: 'Nhập tin nhắn…', ar: 'اكتب رسالة…', fr: 'Tapez un message…', ko: '메시지 입력…',
    id: 'Ketik pesan…', th: 'พิมพ์ข้อความ…',
  },

  // ── Appointments ─────────────────────────────────────────────────────────────
  appts_title: {
    en: 'Appointments', es: 'Citas', zh: '预约', tl: 'Mga Appointment',
    vi: 'Lịch hẹn', ar: 'المواعيد', fr: 'Rendez-vous', ko: '예약',
    id: 'Janji Temu', th: 'นัดหมาย',
  },
  appts_request: {
    en: 'Request an appointment', es: 'Solicitar una cita', zh: '申请预约', tl: 'Humiling ng appointment',
    vi: 'Yêu cầu lịch hẹn', ar: 'طلب موعد', fr: 'Demander un rendez-vous', ko: '예약 요청',
    id: 'Minta janji temu', th: 'ขอนัดหมาย',
  },
  appts_upcoming: {
    en: 'Upcoming', es: 'Próximas', zh: '即将到来', tl: 'Paparating',
    vi: 'Sắp tới', ar: 'القادمة', fr: 'À venir', ko: '예정',
    id: 'Mendatang', th: 'ที่กำลังจะมาถึง',
  },
  appts_past: {
    en: 'Past', es: 'Pasadas', zh: '过去', tl: 'Nakaraang',
    vi: 'Đã qua', ar: 'الماضية', fr: 'Passés', ko: '지난',
    id: 'Sebelumnya', th: 'ที่ผ่านมา',
  },
  appts_confirmed: {
    en: 'Confirmed', es: 'Confirmada', zh: '已确认', tl: 'Nakumpirma',
    vi: 'Đã xác nhận', ar: 'مؤكد', fr: 'Confirmé', ko: '확정',
    id: 'Dikonfirmasi', th: 'ยืนยันแล้ว',
  },
  appts_requested: {
    en: 'Requested', es: 'Solicitada', zh: '已申请', tl: 'Hiniling',
    vi: 'Đã yêu cầu', ar: 'مطلوب', fr: 'Demandé', ko: '요청됨',
    id: 'Diminta', th: 'ร้องขอแล้ว',
  },
  appts_none: {
    en: 'No appointments yet.', es: 'Aún no hay citas.', zh: '暂无预约。', tl: 'Wala pang appointment.',
    vi: 'Chưa có lịch hẹn.', ar: 'لا توجد مواعيد بعد.', fr: 'Aucun rendez-vous pour le moment.', ko: '아직 예약이 없습니다.',
    id: 'Belum ada janji temu.', th: 'ยังไม่มีนัดหมาย',
  },

  // ── Account ──────────────────────────────────────────────────────────────────
  account_title: {
    en: 'Account Settings', es: 'Configuración de cuenta', zh: '账户设置', tl: 'Mga Setting ng Account',
    vi: 'Cài đặt Tài khoản', ar: 'إعدادات الحساب', fr: 'Paramètres du compte', ko: '계정 설정',
    id: 'Pengaturan Akun', th: 'การตั้งค่าบัญชี',
  },
  account_change_pw: {
    en: 'Change Password', es: 'Cambiar contraseña', zh: '修改密码', tl: 'Baguhin ang Password',
    vi: 'Đổi Mật khẩu', ar: 'تغيير كلمة المرور', fr: 'Changer le mot de passe', ko: '비밀번호 변경',
    id: 'Ganti Kata Sandi', th: 'เปลี่ยนรหัสผ่าน',
  },
  account_current_pw: {
    en: 'Current password', es: 'Contraseña actual', zh: '当前密码', tl: 'Kasalukuyang password',
    vi: 'Mật khẩu hiện tại', ar: 'كلمة المرور الحالية', fr: 'Mot de passe actuel', ko: '현재 비밀번호',
    id: 'Kata sandi saat ini', th: 'รหัสผ่านปัจจุบัน',
  },
  account_new_pw: {
    en: 'New password', es: 'Nueva contraseña', zh: '新密码', tl: 'Bagong password',
    vi: 'Mật khẩu mới', ar: 'كلمة المرور الجديدة', fr: 'Nouveau mot de passe', ko: '새 비밀번호',
    id: 'Kata sandi baru', th: 'รหัสผ่านใหม่',
  },
  account_confirm_pw: {
    en: 'Confirm new password', es: 'Confirmar nueva contraseña', zh: '确认新密码', tl: 'Kumpirmahin ang bagong password',
    vi: 'Xác nhận mật khẩu mới', ar: 'تأكيد كلمة المرور الجديدة', fr: 'Confirmer le nouveau mot de passe', ko: '새 비밀번호 확인',
    id: 'Konfirmasi kata sandi baru', th: 'ยืนยันรหัสผ่านใหม่',
  },
  account_update_pw: {
    en: 'Update Password', es: 'Actualizar contraseña', zh: '更新密码', tl: 'I-update ang Password',
    vi: 'Cập nhật Mật khẩu', ar: 'تحديث كلمة المرور', fr: 'Mettre à jour le mot de passe', ko: '비밀번호 업데이트',
    id: 'Perbarui Kata Sandi', th: 'อัปเดตรหัสผ่าน',
  },

  // ── Discharge survey ─────────────────────────────────────────────────────────
  survey_title: {
    en: "You've been discharged", es: 'Ha sido dado de alta', zh: '您已出院',
    tl: 'Napalabas na kayo', vi: 'Bạn đã được xuất viện',
    ar: 'تم الإفراج عنك', fr: 'Vous avez été libéré', ko: '퇴원하셨습니다',
    id: 'Anda telah dipulangkan', th: 'คุณได้รับการจำหน่ายแล้ว',
  },
  survey_rating: {
    en: 'Overall rating', es: 'Puntuación general', zh: '总体评分', tl: 'Pangkalahatang rating',
    vi: 'Đánh giá tổng thể', ar: 'التقييم العام', fr: 'Note générale', ko: '전체 평점',
    id: 'Penilaian keseluruhan', th: 'คะแนนโดยรวม',
  },
  survey_comments: {
    en: 'Any comments? (optional)', es: '¿Algún comentario? (opcional)', zh: '有什么意见？（可选）',
    tl: 'May mga komento? (opsyonal)', vi: 'Có bình luận không? (tùy chọn)',
    ar: 'هل لديك تعليقات؟ (اختياري)', fr: 'Des commentaires ? (facultatif)', ko: '의견이 있으신가요? (선택)',
    id: 'Ada komentar? (opsional)', th: 'มีความคิดเห็น? (ถ้ามี)',
  },
  survey_submit: {
    en: 'Submit Feedback', es: 'Enviar comentarios', zh: '提交反馈', tl: 'Isumite ang Feedback',
    vi: 'Gửi Phản hồi', ar: 'إرسال التغذية الراجعة', fr: 'Envoyer les commentaires', ko: '피드백 제출',
    id: 'Kirim Umpan Balik', th: 'ส่งความคิดเห็น',
  },
  survey_thankyou: {
    en: 'Thank you for your feedback!', es: '¡Gracias por sus comentarios!', zh: '感谢您的反馈！',
    tl: 'Salamat sa inyong feedback!', vi: 'Cảm ơn bạn đã phản hồi!',
    ar: 'شكراً على تعليقاتك!', fr: 'Merci pour vos commentaires !', ko: '피드백 감사합니다!',
    id: 'Terima kasih atas umpan balik Anda!', th: 'ขอบคุณสำหรับความคิดเห็นของคุณ!',
  },

  // ── Therapist nav / app ──────────────────────────────────────────────────────
  app_overview: {
    en: 'Overview', es: 'Resumen', zh: '概览', tl: 'Pangkalahatang-tanaw',
    vi: 'Tổng quan', ar: 'نظرة عامة', fr: 'Aperçu', ko: '개요',
    id: 'Ikhtisar', th: 'ภาพรวม',
  },
  app_patients: {
    en: 'Patients', es: 'Pacientes', zh: '患者', tl: 'Mga Pasyente',
    vi: 'Bệnh nhân', ar: 'المرضى', fr: 'Patients', ko: '환자',
    id: 'Pasien', th: 'ผู้ป่วย',
  },
  app_appointments: {
    en: 'Appointments', es: 'Citas', zh: '预约', tl: 'Mga Appointment',
    vi: 'Lịch hẹn', ar: 'المواعيد', fr: 'Rendez-vous', ko: '예약',
    id: 'Janji Temu', th: 'นัดหมาย',
  },
  app_messages: {
    en: 'Messages', es: 'Mensajes', zh: '消息', tl: 'Mga Mensahe',
    vi: 'Tin nhắn', ar: 'الرسائل', fr: 'Messages', ko: '메시지',
    id: 'Pesan', th: 'ข้อความ',
  },
  app_staff: {
    en: 'Staff', es: 'Personal', zh: '员工', tl: 'Mga Kawani',
    vi: 'Nhân viên', ar: 'الموظفون', fr: 'Personnel', ko: '직원',
    id: 'Staf', th: 'พนักงาน',
  },
  app_settings: {
    en: 'Settings', es: 'Configuración', zh: '设置', tl: 'Mga Setting',
    vi: 'Cài đặt', ar: 'الإعدادات', fr: 'Paramètres', ko: '설정',
    id: 'Pengaturan', th: 'การตั้งค่า',
  },
  app_billing: {
    en: 'Billing', es: 'Facturación', zh: '账单', tl: 'Billing',
    vi: 'Thanh toán', ar: 'الفوترة', fr: 'Facturation', ko: '청구',
    id: 'Penagihan', th: 'การเรียกเก็บเงิน',
  },

  // ── Therapist billing panel ───────────────────────────────────────────────────
  thbill_title: {
    en: 'Patient Billing', es: 'Facturación del paciente', zh: '患者账单', tl: 'Patient Billing',
    vi: 'Thanh toán Bệnh nhân', ar: 'فوترة المريض', fr: 'Facturation du patient', ko: '환자 청구',
    id: 'Penagihan Pasien', th: 'การเรียกเก็บเงินผู้ป่วย',
  },
  thbill_create: {
    en: 'Create Invoice', es: 'Crear factura', zh: '创建发票', tl: 'Gumawa ng Invoice',
    vi: 'Tạo Hóa đơn', ar: 'إنشاء فاتورة', fr: 'Créer une facture', ko: '청구서 생성',
    id: 'Buat Faktur', th: 'สร้างใบแจ้งหนี้',
  },
  thbill_amount: {
    en: 'Amount ($)', es: 'Importe ($)', zh: '金额 ($)', tl: 'Halaga ($)',
    vi: 'Số tiền ($)', ar: 'المبلغ ($)', fr: 'Montant ($)', ko: '금액 ($)',
    id: 'Jumlah ($)', th: 'จำนวนเงิน ($)',
  },
  thbill_desc: {
    en: 'Description', es: 'Descripción', zh: '描述', tl: 'Paglalarawan',
    vi: 'Mô tả', ar: 'الوصف', fr: 'Description', ko: '설명',
    id: 'Deskripsi', th: 'รายละเอียด',
  },
  thbill_due_date: {
    en: 'Due Date', es: 'Fecha límite', zh: '到期日', tl: 'Takdang Petsa',
    vi: 'Ngày đến hạn', ar: 'تاريخ الاستحقاق', fr: "Date d'échéance", ko: '마감일',
    id: 'Tanggal Jatuh Tempo', th: 'วันครบกำหนด',
  },
  thbill_send_invoice: {
    en: 'Send Invoice', es: 'Enviar factura', zh: '发送发票', tl: 'Magpadala ng Invoice',
    vi: 'Gửi Hóa đơn', ar: 'إرسال الفاتورة', fr: 'Envoyer la facture', ko: '청구서 발송',
    id: 'Kirim Faktur', th: 'ส่งใบแจ้งหนี้',
  },
  thbill_invoice_history: {
    en: 'Invoice History', es: 'Historial de facturas', zh: '发票历史', tl: 'Kasaysayan ng Invoice',
    vi: 'Lịch sử Hóa đơn', ar: 'سجل الفواتير', fr: 'Historique des factures', ko: '청구 내역',
    id: 'Riwayat Faktur', th: 'ประวัติใบแจ้งหนี้',
  },
  thbill_no_invoices: {
    en: 'No invoices yet.', es: 'Aún no hay facturas.', zh: '暂无发票。', tl: 'Wala pang invoice.',
    vi: 'Chưa có hóa đơn.', ar: 'لا توجد فواتير بعد.', fr: 'Aucune facture pour le moment.', ko: '아직 청구서가 없습니다.',
    id: 'Belum ada faktur.', th: 'ยังไม่มีใบแจ้งหนี้',
  },
  thbill_revenue: {
    en: 'Revenue Received', es: 'Ingresos recibidos', zh: '已收收入', tl: 'Kita na Natanggap',
    vi: 'Doanh thu Nhận được', ar: 'الإيرادات المستلمة', fr: 'Revenus reçus', ko: '수령 수익',
    id: 'Pendapatan Diterima', th: 'รายได้ที่ได้รับ',
  },
  thbill_outstanding: {
    en: 'Outstanding', es: 'Pendiente de pago', zh: '未付款', tl: 'Hindi pa nababayaran',
    vi: 'Chưa thanh toán', ar: 'مستحق', fr: 'Impayé', ko: '미결제',
    id: 'Belum Dibayar', th: 'ยังไม่ได้ชำระ',
  },
  thbill_mark_paid: {
    en: 'Mark Paid', es: 'Marcar pagado', zh: '标记已付', tl: 'Markahan Bayad',
    vi: 'Đánh dấu Đã thanh toán', ar: 'وضع علامة مدفوع', fr: 'Marquer payé', ko: '결제 완료 표시',
    id: 'Tandai Dibayar', th: 'ทำเครื่องหมายว่าจ่ายแล้ว',
  },

  // ── Language switcher ────────────────────────────────────────────────────────
  lang_label: {
    en: 'Language', es: 'Idioma', zh: '语言', tl: 'Wika',
    vi: 'Ngôn ngữ', ar: 'اللغة', fr: 'Langue', ko: '언어',
    id: 'Bahasa', th: 'ภาษา',
  },

  // ── Patient view — Home ──────────────────────────────────────────────────────
  home_welcome_back: {
    en: 'Welcome back', es: 'Bienvenido de nuevo', zh: '欢迎回来', tl: 'Maligayang pagbabalik',
    vi: 'Chào mừng trở lại', ar: 'مرحباً بعودتك', fr: 'Bon retour', ko: '다시 오신 것을 환영합니다',
    id: 'Selamat datang kembali', th: 'ยินดีต้อนรับกลับ',
  },
  home_recovery_portal: {
    en: 'Recovery Portal', es: 'Portal de Recuperación', zh: '康复门户', tl: 'Portal ng Pagbabawi',
    vi: 'Cổng Phục hồi', ar: 'بوابة التعافي', fr: 'Portail de Rétablissement', ko: '회복 포털',
    id: 'Portal Pemulihan', th: 'พอร์ทัลการฟื้นตัว',
  },
  home_next_appt: {
    en: 'Next appointment', es: 'Próxima cita', zh: '下次预约', tl: 'Susunod na appointment',
    vi: 'Lịch hẹn tiếp theo', ar: 'الموعد القادم', fr: 'Prochain rendez-vous', ko: '다음 예약',
    id: 'Janji berikutnya', th: 'นัดหมายครั้งถัดไป',
  },
  home_confirmed: {
    en: 'Confirmed', es: 'Confirmado', zh: '已确认', tl: 'Nakumpirma',
    vi: 'Đã xác nhận', ar: 'مؤكد', fr: 'Confirmé', ko: '확인됨',
    id: 'Dikonfirmasi', th: 'ยืนยันแล้ว',
  },
  home_quick_access: {
    en: 'Quick access', es: 'Acceso rápido', zh: '快速访问', tl: 'Mabilis na access',
    vi: 'Truy cập nhanh', ar: 'وصول سريع', fr: 'Accès rapide', ko: '빠른 접근',
    id: 'Akses cepat', th: 'เข้าถึงด่วน',
  },
  home_chat_therapist: {
    en: 'Chat with your therapist', es: 'Chatea con tu terapeuta', zh: '与治疗师聊天', tl: 'Makipag-chat sa iyong therapist',
    vi: 'Trò chuyện với nhà trị liệu', ar: 'تحدث مع معالجك', fr: 'Discutez avec votre thérapeute', ko: '치료사와 채팅',
    id: 'Chat dengan terapis Anda', th: 'แชทกับนักบำบัดของคุณ',
  },
  home_done_today: {
    en: 'done today', es: 'hechos hoy', zh: '今日完成', tl: 'tapos na ngayon',
    vi: 'hoàn thành hôm nay', ar: 'منجز اليوم', fr: 'faits aujourd\'hui', ko: '오늘 완료',
    id: 'selesai hari ini', th: 'เสร็จวันนี้',
  },
  home_of_completed: {
    en: 'completed', es: 'completadas', zh: '已完成', tl: 'nakumpleto',
    vi: 'đã hoàn thành', ar: 'مكتمل', fr: 'complétés', ko: '완료됨',
    id: 'selesai', th: 'เสร็จสิ้น',
  },
  home_request_session: {
    en: 'Request a session', es: 'Solicitar una sesión', zh: '请求预约', tl: 'Humiling ng session',
    vi: 'Yêu cầu buổi hẹn', ar: 'طلب جلسة', fr: 'Demander une séance', ko: '세션 요청',
    id: 'Minta sesi', th: 'ขอนัดหมาย',
  },
  home_latest_session: {
    en: 'Latest session note', es: 'Nota de sesión más reciente', zh: '最新会话记录', tl: 'Pinakabagong tala ng session',
    vi: 'Ghi chú buổi gần nhất', ar: 'ملاحظة الجلسة الأخيرة', fr: 'Dernière note de séance', ko: '최근 세션 노트',
    id: 'Catatan sesi terbaru', th: 'บันทึกเซสชั่นล่าสุด',
  },
  home_care_complete: {
    en: 'Your care is complete', es: 'Tu tratamiento está completo', zh: '您的治疗已完成', tl: 'Kumpleto na ang iyong pag-aalaga',
    vi: 'Việc chăm sóc của bạn đã hoàn thành', ar: 'اكتملت رعايتك', fr: 'Votre soin est terminé', ko: '치료가 완료되었습니다',
    id: 'Perawatan Anda selesai', th: 'การดูแลของคุณเสร็จสิ้น',
  },
  home_how_experience: {
    en: 'How was your experience?', es: '¿Cómo fue tu experiencia?', zh: '您的体验如何？', tl: 'Kumusta ang iyong karanasan?',
    vi: 'Trải nghiệm của bạn như thế nào?', ar: 'كيف كانت تجربتك؟', fr: 'Comment s\'est passée votre expérience?', ko: '경험이 어떠셨나요?',
    id: 'Bagaimana pengalaman Anda?', th: 'ประสบการณ์ของคุณเป็นอย่างไร?',
  },
  home_discharged_congrats: {
    en: "You've been discharged — congratulations on your recovery! We'd love to hear how it went.",
    es: 'Has sido dado de alta — ¡felicitaciones por tu recuperación! Nos encantaría saber cómo te fue.',
    zh: '您已出院——恭喜康复！我们很想了解您的康复情况。',
    tl: 'Nai-discharge ka na — binabati kita sa iyong pagbabawi! Gusto naming marinig kung paano ito nagtagumpay.',
    vi: 'Bạn đã được xuất viện — chúc mừng bạn đã hồi phục! Chúng tôi muốn nghe về quá trình đó.',
    ar: 'لقد تم خروجك — تهانينا على تعافيك! نود سماع كيف سارت الأمور.',
    fr: 'Vous avez été libéré — félicitations pour votre rétablissement! Nous aimerions savoir comment ça s\'est passé.',
    ko: '퇴원하셨습니다 — 회복을 축하드립니다! 어떻게 진행되었는지 듣고 싶습니다.',
    id: 'Anda telah dipulangkan — selamat atas pemulihan Anda! Kami ingin mendengar bagaimana hasilnya.',
    th: 'คุณได้รับการจำหน่ายแล้ว — ขอแสดงความยินดีกับการฟื้นตัวของคุณ! เราอยากฟังว่ามันเป็นอย่างไร',
  },
  home_overall_rating: {
    en: 'Overall rating', es: 'Calificación general', zh: '总体评分', tl: 'Pangkalahatang rating',
    vi: 'Đánh giá tổng thể', ar: 'التقييم العام', fr: 'Note générale', ko: '전체 평점',
    id: 'Penilaian keseluruhan', th: 'คะแนนโดยรวม',
  },
  home_any_comments: {
    en: 'Any comments? (optional)', es: '¿Algún comentario? (opcional)', zh: '有任何评论吗？（可选）', tl: 'May mga komento? (opsyonal)',
    vi: 'Có nhận xét nào không? (tùy chọn)', ar: 'أي تعليقات؟ (اختياري)', fr: 'Des commentaires? (optionnel)', ko: '의견이 있으신가요? (선택사항)',
    id: 'Ada komentar? (opsional)', th: 'มีความคิดเห็นอะไรไหม? (ไม่บังคับ)',
  },
  home_feedback_placeholder: {
    en: 'Tell us what went well or how we can improve…',
    es: 'Cuéntanos qué salió bien o cómo podemos mejorar…',
    zh: '请告诉我们哪些方面做得好或如何改进……',
    tl: 'Sabihin sa amin kung ano ang naging maayos o kung paano kami mapabuti…',
    vi: 'Cho chúng tôi biết điều gì diễn ra tốt hoặc chúng tôi có thể cải thiện như thế nào…',
    ar: 'أخبرنا بما سار على ما يرام أو كيف يمكننا التحسين…',
    fr: 'Dites-nous ce qui s\'est bien passé ou comment nous pouvons nous améliorer…',
    ko: '잘 된 점이나 개선할 점을 알려주세요…',
    id: 'Ceritakan apa yang berjalan baik atau bagaimana kami dapat meningkatkan…',
    th: 'บอกเราว่าอะไรดีหรือเราจะปรับปรุงได้อย่างไร…',
  },
  home_submit_feedback: {
    en: 'Submit Feedback', es: 'Enviar comentarios', zh: '提交反馈', tl: 'Isumite ang Feedback',
    vi: 'Gửi phản hồi', ar: 'إرسال التقييم', fr: 'Soumettre un avis', ko: '피드백 제출',
    id: 'Kirim Masukan', th: 'ส่งคำติชม',
  },
  home_submitting: {
    en: 'Submitting…', es: 'Enviando…', zh: '提交中…', tl: 'Nagsusumite…',
    vi: 'Đang gửi…', ar: 'جارٍ الإرسال…', fr: 'Envoi en cours…', ko: '제출 중…',
    id: 'Mengirim…', th: 'กำลังส่ง…',
  },
  home_thanks_feedback: {
    en: 'Thank you for your feedback!', es: '¡Gracias por tus comentarios!', zh: '感谢您的反馈！', tl: 'Salamat sa iyong feedback!',
    vi: 'Cảm ơn phản hồi của bạn!', ar: 'شكراً لتقييمك!', fr: 'Merci pour votre avis!', ko: '피드백 감사합니다!',
    id: 'Terima kasih atas masukan Anda!', th: 'ขอบคุณสำหรับคำติชมของคุณ!',
  },
  home_helps_improving: {
    en: 'It helps us continue improving care for everyone.',
    es: 'Nos ayuda a seguir mejorando la atención para todos.',
    zh: '它帮助我们不断改善对所有人的护理。',
    tl: 'Nakakatulong ito sa amin na patuloy na mapabuti ang pag-aalaga para sa lahat.',
    vi: 'Nó giúp chúng tôi tiếp tục cải thiện dịch vụ chăm sóc cho mọi người.',
    ar: 'يساعدنا على الاستمرار في تحسين الرعاية للجميع.',
    fr: 'Cela nous aide à continuer à améliorer les soins pour tous.',
    ko: '모든 분들을 위한 치료 개선을 계속하는 데 도움이 됩니다.',
    id: 'Ini membantu kami terus meningkatkan perawatan untuk semua orang.',
    th: 'มันช่วยให้เราปรับปรุงการดูแลสำหรับทุกคนต่อไป',
  },

  // ── Patient view — My Health ─────────────────────────────────────────────────
  health_overview_heading: {
    en: 'Recovery Overview', es: 'Resumen de Recuperación', zh: '康复概览', tl: 'Pangkalahatang-ideya ng Pagbabawi',
    vi: 'Tổng quan Phục hồi', ar: 'نظرة عامة على التعافي', fr: 'Vue d\'ensemble de la Rétablissement', ko: '회복 개요',
    id: 'Ikhtisar Pemulihan', th: 'ภาพรวมการฟื้นตัว',
  },
  health_pain_down: {
    en: 'Pain down', es: 'Dolor reducido', zh: '疼痛减少', tl: 'Bumaba ang sakit',
    vi: 'Giảm đau', ar: 'انخفاض الألم', fr: 'Douleur réduite', ko: '통증 감소',
    id: 'Nyeri berkurang', th: 'ความเจ็บปวดลดลง',
  },
  health_since_first: {
    en: 'Since your first session — that\'s real, measurable progress.',
    es: 'Desde tu primera sesión — ese es un progreso real y medible.',
    zh: '自第一次治疗以来——这是真实可量化的进步。',
    tl: 'Mula sa iyong unang session — tunay at nasusukat na pag-unlad iyon.',
    vi: 'Kể từ buổi đầu tiên — đó là tiến bộ thực sự, có thể đo lường được.',
    ar: 'منذ جلستك الأولى — هذا تقدم حقيقي وقابل للقياس.',
    fr: 'Depuis votre première séance — c\'est un progrès réel et mesurable.',
    ko: '첫 번째 세션 이후 — 실제로 측정 가능한 진전입니다.',
    id: 'Sejak sesi pertama Anda — itu adalah kemajuan nyata yang terukur.',
    th: 'นับตั้งแต่เซสชั่นแรกของคุณ — นั่นคือความก้าวหน้าที่แท้จริงและวัดได้',
  },
  health_pain_chart: {
    en: 'Pain score over time', es: 'Puntuación de dolor a lo largo del tiempo', zh: '疼痛评分变化', tl: 'Marka ng sakit sa paglipas ng panahon',
    vi: 'Điểm đau theo thời gian', ar: 'درجة الألم عبر الزمن', fr: 'Score de douleur au fil du temps', ko: '시간에 따른 통증 점수',
    id: 'Skor nyeri dari waktu ke waktu', th: 'คะแนนความเจ็บปวดตามเวลา',
  },
  health_lower_better: {
    en: 'Lower is better', es: 'Menor es mejor', zh: '越低越好', tl: 'Mas mababa ay mas mabuti',
    vi: 'Thấp hơn là tốt hơn', ar: 'الأقل أفضل', fr: 'Plus bas c\'est mieux', ko: '낮을수록 좋음',
    id: 'Lebih rendah lebih baik', th: 'ยิ่งต่ำยิ่งดี',
  },
  health_no_data: {
    en: 'Not enough session data yet.', es: 'Aún no hay suficientes datos de sesión.', zh: '尚无足够的会话数据。', tl: 'Hindi pa sapat ang datos ng session.',
    vi: 'Chưa có đủ dữ liệu buổi.', ar: 'لا توجد بيانات جلسة كافية بعد.', fr: 'Pas encore assez de données de séance.', ko: '아직 세션 데이터가 충분하지 않습니다.',
    id: 'Belum cukup data sesi.', th: 'ยังไม่มีข้อมูลเซสชั่นเพียงพอ',
  },
  health_session_notes: {
    en: 'Session notes', es: 'Notas de sesión', zh: '会话记录', tl: 'Mga tala ng session',
    vi: 'Ghi chú buổi', ar: 'ملاحظات الجلسة', fr: 'Notes de séance', ko: '세션 노트',
    id: 'Catatan sesi', th: 'บันทึกเซสชั่น',
  },
  health_session_label: {
    en: 'Session', es: 'Sesión', zh: '会话', tl: 'Session',
    vi: 'Buổi', ar: 'الجلسة', fr: 'Séance', ko: '세션',
    id: 'Sesi', th: 'เซสชั่น',
  },

  // ── Patient view — Exercises ─────────────────────────────────────────────────
  exer_home_program: {
    en: 'Your Home Program', es: 'Tu Programa en Casa', zh: '您的家庭训练', tl: 'Ang Iyong Home Program',
    vi: 'Chương trình Tại nhà của bạn', ar: 'برنامجك المنزلي', fr: 'Votre Programme à Domicile', ko: '홈 프로그램',
    id: 'Program Rumah Anda', th: 'โปรแกรมที่บ้านของคุณ',
  },
  exer_no_exercises: {
    en: 'No exercises added yet.', es: 'Aún no se han agregado ejercicios.', zh: '尚未添加练习。', tl: 'Wala pang mga ehersisyong idinagdag.',
    vi: 'Chưa có bài tập nào được thêm.', ar: 'لم تتم إضافة تمارين بعد.', fr: 'Aucun exercice ajouté pour le moment.', ko: '아직 추가된 운동이 없습니다.',
    id: 'Belum ada latihan ditambahkan.', th: 'ยังไม่มีการเพิ่มแบบฝึกหัด',
  },
  exer_sets: {
    en: 'sets', es: 'series', zh: '组', tl: 'mga set',
    vi: 'hiệp', ar: 'مجموعات', fr: 'séries', ko: '세트',
    id: 'set', th: 'เซต',
  },
  exer_reps: {
    en: 'reps', es: 'repeticiones', zh: '次', tl: 'mga ulit',
    vi: 'lần', ar: 'تكرارات', fr: 'rép.', ko: '회',
    id: 'rep', th: 'ครั้ง',
  },
  exer_done_badge: {
    en: 'Done', es: 'Listo', zh: '完成', tl: 'Tapos',
    vi: 'Xong', ar: 'تم', fr: 'Fait', ko: '완료',
    id: 'Selesai', th: 'เสร็จ',
  },
  exer_log_it: {
    en: 'Log it', es: 'Registrar', zh: '记录', tl: 'I-log',
    vi: 'Ghi lại', ar: 'سجّل', fr: 'Enregistrer', ko: '기록하기',
    id: 'Catat', th: 'บันทึก',
  },
  exer_watch_video: {
    en: 'Watch video', es: 'Ver video', zh: '观看视频', tl: 'Panoorin ang video',
    vi: 'Xem video', ar: 'شاهد الفيديو', fr: 'Voir la vidéo', ko: '동영상 보기',
    id: 'Tonton video', th: 'ดูวิดีโอ',
  },
  exer_ask_caleb: {
    en: 'Ask Caleb', es: 'Preguntar a Caleb', zh: '询问Caleb', tl: 'Tanungin si Caleb',
    vi: 'Hỏi Caleb', ar: 'اسأل كاليب', fr: 'Demander à Caleb', ko: 'Caleb에게 질문',
    id: 'Tanya Caleb', th: 'ถาม Caleb',
  },
  exer_difficulty: {
    en: 'Difficulty', es: 'Dificultad', zh: '难度', tl: 'Kahirapan',
    vi: 'Độ khó', ar: 'الصعوبة', fr: 'Difficulté', ko: '난이도',
    id: 'Kesulitan', th: 'ความยาก',
  },
  exer_recent_logs: {
    en: 'Recent logs', es: 'Registros recientes', zh: '最近记录', tl: 'Mga kamakailang log',
    vi: 'Nhật ký gần đây', ar: 'السجلات الأخيرة', fr: 'Journaux récents', ko: '최근 기록',
    id: 'Log terbaru', th: 'บันทึกล่าสุด',
  },

  // ── Patient view — Goals ─────────────────────────────────────────────────────
  goals_milestones: {
    en: 'Recovery Milestones', es: 'Hitos de Recuperación', zh: '康复里程碑', tl: 'Mga Milestone ng Pagbabawi',
    vi: 'Cột mốc Phục hồi', ar: 'معالم التعافي', fr: 'Étapes de Rétablissement', ko: '회복 마일스톤',
    id: 'Pencapaian Pemulihan', th: 'เหตุการณ์สำคัญในการฟื้นตัว',
  },
  goals_completed_label: {
    en: 'Completed', es: 'Completadas', zh: '已完成', tl: 'Nakumpleto',
    vi: 'Đã hoàn thành', ar: 'مكتملة', fr: 'Complétés', ko: '완료됨',
    id: 'Selesai', th: 'เสร็จสิ้น',
  },
  goals_in_progress: {
    en: 'In progress', es: 'En progreso', zh: '进行中', tl: 'Nasa proseso',
    vi: 'Đang tiến hành', ar: 'قيد التنفيذ', fr: 'En cours', ko: '진행 중',
    id: 'Sedang berlangsung', th: 'กำลังดำเนินการ',
  },
  goals_no_goals: {
    en: 'No goals set yet.', es: 'Aún no se han establecido metas.', zh: '尚未设定目标。', tl: 'Wala pang mga layuning itinakda.',
    vi: 'Chưa đặt mục tiêu nào.', ar: 'لم يتم تحديد أهداف بعد.', fr: 'Aucun objectif fixé pour le moment.', ko: '아직 설정된 목표가 없습니다.',
    id: 'Belum ada tujuan yang ditetapkan.', th: 'ยังไม่ได้ตั้งเป้าหมาย',
  },
  goals_target: {
    en: 'Target:', es: 'Meta:', zh: '目标：', tl: 'Target:',
    vi: 'Mục tiêu:', ar: 'الهدف:', fr: 'Objectif:', ko: '목표:',
    id: 'Target:', th: 'เป้าหมาย:',
  },
  goals_done_badge: {
    en: 'Done', es: 'Cumplida', zh: '完成', tl: 'Tapos',
    vi: 'Xong', ar: 'منجز', fr: 'Accompli', ko: '완료',
    id: 'Selesai', th: 'เสร็จ',
  },

  // ── Patient view — Messages ──────────────────────────────────────────────────
  msg_your_therapist: {
    en: 'Your Therapist', es: 'Tu Terapeuta', zh: '您的治疗师', tl: 'Ang Iyong Therapist',
    vi: 'Nhà trị liệu của bạn', ar: 'معالجك', fr: 'Votre Thérapeute', ko: '담당 치료사',
    id: 'Terapis Anda', th: 'นักบำบัดของคุณ',
  },
  msg_private_secure: {
    en: 'Messages are private and secure.', es: 'Los mensajes son privados y seguros.', zh: '消息是私密且安全的。', tl: 'Ang mga mensahe ay pribado at ligtas.',
    vi: 'Tin nhắn là riêng tư và bảo mật.', ar: 'الرسائل خاصة وآمنة.', fr: 'Les messages sont privés et sécurisés.', ko: '메시지는 비공개이며 안전합니다.',
    id: 'Pesan bersifat pribadi dan aman.', th: 'ข้อความเป็นส่วนตัวและปลอดภัย',
  },

  // ── Patient view — Appointments ──────────────────────────────────────────────
  appt_schedule_heading: {
    en: 'Schedule a Session', es: 'Programar una Sesión', zh: '预约会话', tl: 'Mag-iskedyul ng Session',
    vi: 'Đặt lịch Buổi', ar: 'جدولة جلسة', fr: 'Planifier une Séance', ko: '세션 예약',
    id: 'Jadwalkan Sesi', th: 'กำหนดตารางเซสชั่น',
  },
  appt_request: {
    en: 'Request a session', es: 'Solicitar una sesión', zh: '申请预约', tl: 'Humiling ng session',
    vi: 'Yêu cầu một buổi', ar: 'طلب جلسة', fr: 'Demander une séance', ko: '세션 요청',
    id: 'Minta sesi', th: 'ขอนัดหมาย',
  },
  appt_date: {
    en: 'Date', es: 'Fecha', zh: '日期', tl: 'Petsa',
    vi: 'Ngày', ar: 'التاريخ', fr: 'Date', ko: '날짜',
    id: 'Tanggal', th: 'วันที่',
  },
  appt_preferred_time: {
    en: 'Preferred time', es: 'Hora preferida', zh: '首选时间', tl: 'Gustong oras',
    vi: 'Giờ ưa thích', ar: 'الوقت المفضل', fr: 'Heure préférée', ko: '선호 시간',
    id: 'Waktu yang diinginkan', th: 'เวลาที่ต้องการ',
  },
  appt_select_time: {
    en: 'Select a time', es: 'Seleccionar una hora', zh: '选择时间', tl: 'Pumili ng oras',
    vi: 'Chọn thời gian', ar: 'اختر وقتاً', fr: 'Choisir une heure', ko: '시간 선택',
    id: 'Pilih waktu', th: 'เลือกเวลา',
  },
  appt_notes: {
    en: 'Notes (optional)', es: 'Notas (opcional)', zh: '备注（可选）', tl: 'Mga tala (opsyonal)',
    vi: 'Ghi chú (tùy chọn)', ar: 'ملاحظات (اختياري)', fr: 'Notes (optionnel)', ko: '메모 (선택사항)',
    id: 'Catatan (opsional)', th: 'หมายเหตุ (ไม่บังคับ)',
  },
  appt_notes_placeholder: {
    en: 'e.g. Morning preferred, right knee pain…',
    es: 'p. ej. Prefiero las mañanas, dolor en la rodilla derecha…',
    zh: '例如：优先选择早上，右膝疼痛……',
    tl: 'hal. Mas gusto sa umaga, sakit sa kanang tuhod…',
    vi: 'vd. Ưu tiên buổi sáng, đau đầu gối phải…',
    ar: 'مثال: يُفضَّل الصباح، ألم في الركبة اليمنى…',
    fr: 'ex. Préférence pour le matin, douleur au genou droit…',
    ko: '예: 오전 선호, 오른쪽 무릎 통증…',
    id: 'mis. Pagi lebih disukai, nyeri lutut kanan…',
    th: 'เช่น ต้องการช่วงเช้า ปวดเข่าขวา…',
  },
  appt_send: {
    en: 'Send Request', es: 'Enviar solicitud', zh: '发送请求', tl: 'Ipadala ang Kahilingan',
    vi: 'Gửi yêu cầu', ar: 'إرسال الطلب', fr: 'Envoyer la demande', ko: '요청 보내기',
    id: 'Kirim Permintaan', th: 'ส่งคำขอ',
  },
  appt_sent_msg: {
    en: 'Request sent — your therapist will confirm shortly.',
    es: 'Solicitud enviada — tu terapeuta confirmará en breve.',
    zh: '请求已发送——您的治疗师将很快确认。',
    tl: 'Ipinadala ang kahilingan — kumpirmahin ng iyong therapist sa lalong madaling panahon.',
    vi: 'Đã gửi yêu cầu — nhà trị liệu của bạn sẽ xác nhận sớm.',
    ar: 'تم إرسال الطلب — سيؤكد معالجك قريباً.',
    fr: 'Demande envoyée — votre thérapeute confirmera bientôt.',
    ko: '요청이 전송되었습니다 — 치료사가 곧 확인할 것입니다.',
    id: 'Permintaan terkirim — terapis Anda akan segera mengkonfirmasi.',
    th: 'ส่งคำขอแล้ว — นักบำบัดของคุณจะยืนยันในไม่ช้า',
  },
  appt_error_msg: {
    en: 'Something went wrong. Please try again.',
    es: 'Algo salió mal. Por favor, intenta de nuevo.',
    zh: '出了些问题。请再试一次。',
    tl: 'May nangyaring mali. Pakisubukan muli.',
    vi: 'Có lỗi xảy ra. Vui lòng thử lại.',
    ar: 'حدث خطأ ما. يرجى المحاولة مرة أخرى.',
    fr: 'Une erreur s\'est produite. Veuillez réessayer.',
    ko: '문제가 발생했습니다. 다시 시도해 주세요.',
    id: 'Ada yang salah. Silakan coba lagi.',
    th: 'เกิดข้อผิดพลาด กรุณาลองอีกครั้ง',
  },
  appt_select_date_time: {
    en: 'Please select a date and time.',
    es: 'Por favor selecciona una fecha y hora.',
    zh: '请选择日期和时间。',
    tl: 'Mangyaring pumili ng petsa at oras.',
    vi: 'Vui lòng chọn ngày và giờ.',
    ar: 'يرجى تحديد التاريخ والوقت.',
    fr: 'Veuillez sélectionner une date et une heure.',
    ko: '날짜와 시간을 선택해 주세요.',
    id: 'Silakan pilih tanggal dan waktu.',
    th: 'กรุณาเลือกวันที่และเวลา',
  },
  appt_your_appointments: {
    en: 'Your appointments', es: 'Tus citas', zh: '您的预约', tl: 'Ang iyong mga appointment',
    vi: 'Lịch hẹn của bạn', ar: 'مواعيدك', fr: 'Vos rendez-vous', ko: '내 예약',
    id: 'Janji temu Anda', th: 'การนัดหมายของคุณ',
  },
  appt_status_confirmed: {
    en: 'Confirmed', es: 'Confirmado', zh: '已确认', tl: 'Nakumpirma',
    vi: 'Đã xác nhận', ar: 'مؤكد', fr: 'Confirmé', ko: '확인됨',
    id: 'Dikonfirmasi', th: 'ยืนยันแล้ว',
  },
  appt_status_cancelled: {
    en: 'Cancelled', es: 'Cancelado', zh: '已取消', tl: 'Kinansela',
    vi: 'Đã hủy', ar: 'ملغى', fr: 'Annulé', ko: '취소됨',
    id: 'Dibatalkan', th: 'ยกเลิกแล้ว',
  },
  appt_status_pending: {
    en: 'Pending', es: 'Pendiente', zh: '待确认', tl: 'Nakabinbin',
    vi: 'Đang chờ', ar: 'قيد الانتظار', fr: 'En attente', ko: '대기 중',
    id: 'Menunggu', th: 'รอดำเนินการ',
  },

  // ── Patient view — Account ───────────────────────────────────────────────────
  acct_settings_heading: {
    en: 'Your Settings', es: 'Tu Configuración', zh: '您的设置', tl: 'Ang Iyong Mga Setting',
    vi: 'Cài đặt của bạn', ar: 'إعداداتك', fr: 'Vos Paramètres', ko: '내 설정',
    id: 'Pengaturan Anda', th: 'การตั้งค่าของคุณ',
  },
  acct_profile: {
    en: 'Profile', es: 'Perfil', zh: '个人资料', tl: 'Profile',
    vi: 'Hồ sơ', ar: 'الملف الشخصي', fr: 'Profil', ko: '프로필',
    id: 'Profil', th: 'โปรไฟล์',
  },
  acct_change_password: {
    en: 'Change password', es: 'Cambiar contraseña', zh: '更改密码', tl: 'Baguhin ang password',
    vi: 'Đổi mật khẩu', ar: 'تغيير كلمة المرور', fr: 'Changer le mot de passe', ko: '비밀번호 변경',
    id: 'Ganti kata sandi', th: 'เปลี่ยนรหัสผ่าน',
  },
  acct_current_pw: {
    en: 'Current password', es: 'Contraseña actual', zh: '当前密码', tl: 'Kasalukuyang password',
    vi: 'Mật khẩu hiện tại', ar: 'كلمة المرور الحالية', fr: 'Mot de passe actuel', ko: '현재 비밀번호',
    id: 'Kata sandi saat ini', th: 'รหัสผ่านปัจจุบัน',
  },
  acct_new_pw: {
    en: 'New password', es: 'Nueva contraseña', zh: '新密码', tl: 'Bagong password',
    vi: 'Mật khẩu mới', ar: 'كلمة مرور جديدة', fr: 'Nouveau mot de passe', ko: '새 비밀번호',
    id: 'Kata sandi baru', th: 'รหัสผ่านใหม่',
  },
  acct_confirm_pw: {
    en: 'Confirm new password', es: 'Confirmar nueva contraseña', zh: '确认新密码', tl: 'Kumpirmahin ang bagong password',
    vi: 'Xác nhận mật khẩu mới', ar: 'تأكيد كلمة المرور الجديدة', fr: 'Confirmer le nouveau mot de passe', ko: '새 비밀번호 확인',
    id: 'Konfirmasi kata sandi baru', th: 'ยืนยันรหัสผ่านใหม่',
  },
  acct_at_least_8: {
    en: 'At least 8 characters', es: 'Al menos 8 caracteres', zh: '至少8个字符', tl: 'Hindi bababa sa 8 character',
    vi: 'Ít nhất 8 ký tự', ar: '٨ أحرف على الأقل', fr: 'Au moins 8 caractères', ko: '최소 8자',
    id: 'Minimal 8 karakter', th: 'อย่างน้อย 8 ตัวอักษร',
  },
  acct_repeat_pw: {
    en: 'Repeat new password', es: 'Repetir nueva contraseña', zh: '重复新密码', tl: 'Ulitin ang bagong password',
    vi: 'Lặp lại mật khẩu mới', ar: 'أعد كلمة المرور الجديدة', fr: 'Répéter le nouveau mot de passe', ko: '새 비밀번호 반복',
    id: 'Ulangi kata sandi baru', th: 'ทำซ้ำรหัสผ่านใหม่',
  },
  acct_update_pw: {
    en: 'Update password', es: 'Actualizar contraseña', zh: '更新密码', tl: 'I-update ang password',
    vi: 'Cập nhật mật khẩu', ar: 'تحديث كلمة المرور', fr: 'Mettre à jour le mot de passe', ko: '비밀번호 업데이트',
    id: 'Perbarui kata sandi', th: 'อัปเดตรหัสผ่าน',
  },
  acct_saving: {
    en: 'Saving…', es: 'Guardando…', zh: '保存中…', tl: 'Nagse-save…',
    vi: 'Đang lưu…', ar: 'جارٍ الحفظ…', fr: 'Enregistrement…', ko: '저장 중…',
    id: 'Menyimpan…', th: 'กำลังบันทึก…',
  },
  acct_session_label: {
    en: 'Session', es: 'Sesión', zh: '会话', tl: 'Session',
    vi: 'Phiên', ar: 'الجلسة', fr: 'Session', ko: '세션',
    id: 'Sesi', th: 'เซสชั่น',
  },
  acct_sign_out: {
    en: 'Sign out', es: 'Cerrar sesión', zh: '退出登录', tl: 'Mag-sign out',
    vi: 'Đăng xuất', ar: 'تسجيل الخروج', fr: 'Se déconnecter', ko: '로그아웃',
    id: 'Keluar', th: 'ออกจากระบบ',
  },
}

// ─── Context ──────────────────────────────────────────────────────────────────
const LanguageContext = createContext()

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => {
    return localStorage.getItem('clearpath_lang') || 'en'
  })

  const currentLang = LANGUAGES.find(l => l.code === lang) || LANGUAGES[0]

  // Apply dir="rtl" to <html> when Arabic is selected
  useEffect(() => {
    document.documentElement.setAttribute('dir', currentLang.dir || 'ltr')
    return () => document.documentElement.setAttribute('dir', 'ltr')
  }, [lang, currentLang.dir])

  const switchLang = (code) => {
    setLang(code)
    localStorage.setItem('clearpath_lang', code)
  }

  // t('key') → translated string for the current language, fallback to English
  const t = (key) => {
    const entry = T[key]
    if (!entry) return key
    return entry[lang] || entry['en'] || key
  }

  return (
    <LanguageContext.Provider value={{ lang, switchLang, t, LANGUAGES, currentLang }}>
      {children}
    </LanguageContext.Provider>
  )
}

export function useLanguage() {
  const ctx = useContext(LanguageContext)
  if (!ctx) throw new Error('useLanguage must be used inside <LanguageProvider>')
  return ctx
}

export default LanguageContext
